/* eslint-disable @nrwl/nx/enforce-module-boundaries */
import { ReactElement } from 'react'
import { LinkBaseProps } from '../components/Link'
import { useSelector } from 'react-redux'
import { selectCrsUser } from '@client-hub/store/slices/metadata/selectors'
import { useModuleCategoryQuery } from '@client-hub/store/api/ccs/react'

import ROUTES from '../../constants/routes'
import { ACTION } from '../../constants/analytics'
import { useTranslation } from 'react-i18next'
import { Icon, IconProps } from '@icanbwell/ui-toolkit'
import {
  getCCSPermissions,
  ClientConfigPermissions,
  ClientReportingPermissions,
  getCRSPermissions,
  hasAnyCRSPermission,
} from '../../utils/permissions'

interface RoutesIconMap {
  [key: string]: string
}

export type SubLinkTypes = Omit<LinkMeta, 'subLinks'>

export type LinkMeta = {
  label: string
  icon: ReactElement
  subLinks?: SubLinkTypes[]
  trackEventAction?: string
} & LinkBaseProps

/**
 * These routes are currently hardcoded here.
 * Reason: Because the same routes are also hardcoded in CCS (and hence are not configurable via any module)
 * we can safely assume that these routes will not change.
 */
const RoutesIconMap: RoutesIconMap = {
  'service-configuration': 'Results',
  translations: 'Convert',
  'feature-configuration': 'Beaker',
  'brand-assets': 'FilesFolder',
}

const baseRouteLink = {
  to: ROUTES.BASE,
  label: 'Home',
  icon: <Icon name="LocationHome" color="white" />,
}

// Hook to provide the title, links, and relative icons associated with CRS (root Hub)
export const useCrsLinks = (): LinkMeta[] => {
  const user = useSelector(selectCrsUser)
  const { t } = useTranslation()
  const links: LinkMeta[] = []

  let permissions = {} as ClientReportingPermissions
  if (user?.permissions) {
    permissions = getCRSPermissions(user?.permissions)
  }
  const hasAnyCrsPermission = hasAnyCRSPermission(permissions)

  if (permissions.reportingSettings) {
    links.push({
      label: t('links.hub.reportingDashboards.label'),
      to: ROUTES.DASHBOARD,
      icon: <Icon name="FinanceChart" color="white" />,
      trackEventAction: ACTION.CLICKED_DASHBOARD,
      subLinks: [
        baseRouteLink,
        {
          to: ROUTES.DASHBOARD,
          label: t('links.hub.reportingDashboards.label'),
          icon: <Icon name="FinanceChart" color="white" />,
        },
      ],
    })
  }
  if (hasAnyCrsPermission) {
    links.push({
      to: ROUTES.HELP,
      label: t('links.hub.helpCenter.label'),
      icon: <Icon name="CommunicationChatsecondary" color="white" />,
      trackEventAction: ACTION.CLICKED_HELP,
      subLinks: [
        baseRouteLink,
        {
          to: ROUTES.HELP,
          label: t('links.hub.helpCenter.label'),
          icon: <Icon name="CommunicationChatsecondary" color="white" />,
        },
      ],
    })
  }
  if (permissions.users && user?.is_admin) {
    links.push({
      to: ROUTES.USERS.BASE,
      label: t('links.hub.users.label'),
      icon: <Icon name="PeopleUsers" color="white" />,
      trackEventAction: ACTION.CLICKED_USERS,
      subLinks: [
        baseRouteLink,
        {
          to: ROUTES.USERS.BASE,
          label: t('links.hub.users.label'),
          icon: <Icon name="PeopleUsers" color="white" />,
        },
      ],
    })
  }
  if (hasAnyCrsPermission && user?.is_parent_admin) {
    links.push({
      to: ROUTES.SETTINGS.BASE,
      label: t('links.hub.settings.label'),
      icon: <Icon name="Settings" color="white" />,
      subLinks: [
        baseRouteLink,
        {
          to: ROUTES.SETTINGS.ADMIN_CONTENT,
          label: t('links.hub.settings.subLinks.content'),
          icon: <Icon name="Settings" color="white" />,
        },
      ],
    })
  }
  return links
}

export const useCcsLinks = (): LinkMeta[] => {
  const user = useSelector(selectCrsUser)
  const { t } = useTranslation()
  const links: LinkMeta[] = []
  let permissions = {} as ClientConfigPermissions
  if (user?.permissions) {
    permissions = getCCSPermissions(user?.permissions)
  }
  const { data: moduleCategories } = useModuleCategoryQuery()

  if (permissions.productConfigurationPermission) {
    links.push({
      to: ROUTES.PRODUCT.BASE,
      label: t('links.hub.productConfiguration.label'),
      icon: <Icon name="List" color="white" />,
      subLinks: [
        baseRouteLink,
        {
          to: ROUTES.PRODUCT.MODULES.BASE,
          label: t('links.hub.productConfiguration.subLinks.modules'),
          icon: <Icon name="FilesFolder" color="white" />,
        },
        {
          to: ROUTES.PRODUCT.GENERIC_MODULE_CONFIGURATION.BASE,
          label: t('links.hub.productConfiguration.subLinks.genericModuleConfiguration'),
          icon: <Icon name="Books" color="white" />,
        },
      ],
    })
  }

  const subLinks: SubLinkTypes[] = []
  if (permissions.siteConfigurationPermission) {
    subLinks.push(baseRouteLink, {
      to: ROUTES.CLIENT.CLIENT_SETTINGS.BASE,
      label: t('links.hub.clientConfiguration.subLinks.clientSettings'),
      icon: <Icon name="Settings" color="white" />,
    })
    if (moduleCategories) {
      moduleCategories.forEach((categoryItem) => {
        const route = categoryItem.display_value.toLowerCase().replace(/\s/g, '-')

        subLinks.push({
          to: ROUTES.CLIENT.CONFIGURATION_ROUTE(route).BASE,
          label: categoryItem.display_value,
          icon: <Icon name={RoutesIconMap[route] as IconProps['name']} color="white" />,
        })
      })
    }
  }

  if (subLinks.length > 0) {
    links.push({
      to: ROUTES.CLIENT.BASE,
      label: t('links.hub.clientConfiguration.label'),
      icon: <Icon name="PeopleUseroutline" color="white" />,
      subLinks: subLinks,
    })
  }
  return links
}
